import React, { useState, useEffect } from 'react';
import { Database, FileDown, Clock } from 'lucide-react';
import supabase from '../services/supabase';
import BackupWarning from './BackupWarning';

const generateSchemaSql = async () => {
  try {
    // Recupera i dati da tutte le tabelle
    const [contatti, magazzini, viaggi] = await Promise.all([
      supabase.from('contatti').select('*'),
      supabase.from('magazzini').select('*'),
      supabase.from('viaggi').select('*')
    ]);

    // Genera lo script SQL
    let sqlScript = `-- Schema del database Truck Transport Management
-- Generato il ${new Date().toLocaleString()}

-- Schema delle tabelle
CREATE TABLE IF NOT EXISTS contatti (
  id UUID PRIMARY KEY,
  nome VARCHAR(100),
  cognome VARCHAR(100),
  azienda VARCHAR(150),
  telefono VARCHAR(50),
  indirizzo TEXT,
  email VARCHAR(100),
  is_cliente BOOLEAN,
  is_vettore BOOLEAN,
  created_at TIMESTAMP,
  updated_at TIMESTAMP
);

CREATE TABLE IF NOT EXISTS magazzini (
  id UUID PRIMARY KEY,
  nome VARCHAR(150),
  created_at TIMESTAMP,
  updated_at TIMESTAMP
);

CREATE TABLE IF NOT EXISTS viaggi (
  id UUID PRIMARY KEY,
  data DATE,
  cliente_id UUID,
  vettore_id UUID,
  magazzino_carico_id UUID,
  magazzino_scarico_id UUID,
  prezzo DECIMAL(10, 2),
  prezzo_vettore DECIMAL(10, 2) NOT NULL DEFAULT 0,
  bancali INTEGER,
  note TEXT,
  created_at TIMESTAMP,
  updated_at TIMESTAMP
);

-- Dati Contatti
`;

    // Funzione di utilità per l'escape delle stringhe SQL
    const escapeSqlString = (str) => {
      if (str === null || str === undefined) return 'NULL';
      return `'${str.toString().replace(/'/g, "''")}'`;
    };

    // Aggiungi dati Contatti
    contatti.data.forEach(contatto => {
      sqlScript += `INSERT INTO contatti (id, nome, cognome, azienda, telefono, indirizzo, email, is_cliente, is_vettore, created_at, updated_at) VALUES (
        '${contatto.id}', 
        ${contatto.nome ? escapeSqlString(contatto.nome) : 'NULL'}, 
        ${contatto.cognome ? escapeSqlString(contatto.cognome) : 'NULL'}, 
        ${contatto.azienda ? escapeSqlString(contatto.azienda) : 'NULL'}, 
        ${contatto.telefono ? escapeSqlString(contatto.telefono) : 'NULL'}, 
        ${contatto.indirizzo ? escapeSqlString(contatto.indirizzo) : 'NULL'}, 
        ${contatto.email ? escapeSqlString(contatto.email) : 'NULL'}, 
        ${contatto.is_cliente}, 
        ${contatto.is_vettore}, 
        '${contatto.created_at}', 
        '${contatto.updated_at}'
      );\n`;
    });

    sqlScript += "\n-- Dati Magazzini\n";
    // Aggiungi dati Magazzini
    magazzini.data.forEach(magazzino => {
      sqlScript += `INSERT INTO magazzini (id, nome, created_at, updated_at) VALUES (
        '${magazzino.id}', 
        ${escapeSqlString(magazzino.nome)}, 
        '${magazzino.created_at}', 
        '${magazzino.updated_at}'
      );\n`;
    });

    sqlScript += "\n-- Dati Viaggi\n";
    // Aggiungi dati Viaggi
    viaggi.data.forEach(viaggio => {
      sqlScript += `INSERT INTO viaggi (id, data, cliente_id, vettore_id, magazzino_carico_id, magazzino_scarico_id, prezzo, prezzo_vettore, bancali, note, created_at, updated_at) VALUES (
        '${viaggio.id}', 
        '${viaggio.data}', 
        ${viaggio.cliente_id ? `'${viaggio.cliente_id}'` : 'NULL'}, 
        ${viaggio.vettore_id ? `'${viaggio.vettore_id}'` : 'NULL'}, 
        ${viaggio.magazzino_carico_id ? `'${viaggio.magazzino_carico_id}'` : 'NULL'}, 
        ${viaggio.magazzino_scarico_id ? `'${viaggio.magazzino_scarico_id}'` : 'NULL'}, 
        ${viaggio.prezzo}, 
        ${viaggio.prezzo_vettore || 0},
        ${viaggio.bancali}, 
        ${viaggio.note ? escapeSqlString(viaggio.note) : 'NULL'},
        '${viaggio.created_at}', 
        '${viaggio.updated_at}'
      );\n`;
    });

    return sqlScript;
  } catch (error) {
    console.error('Errore nel generare lo script SQL:', error);
    return `-- Errore nel generare lo script SQL: ${error.message}`;
  }
};

const AdvancedBackup = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [lastBackup, setLastBackup] = useState(null);
  const [showBackupWarning, setShowBackupWarning] = useState(false);
  const [backupHistory, setBackupHistory] = useState([]);
  const [activeView, setActiveView] = useState('backup');

  // Recupera l'ultimo backup e lo storico
  const fetchBackupInfo = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      // Recupera l'ultimo backup
      const { data: lastBackupData, error: lastBackupError } = await supabase
        .from('backup_log')
        .select('backup_date')
        .eq('user_id', user.id)
        .order('backup_date', { ascending: false })
        .limit(1);

      if (lastBackupError) throw lastBackupError;

      if (lastBackupData.length > 0) {
        const lastBackupDate = new Date(lastBackupData[0].backup_date);
        setLastBackup(lastBackupDate);

        // Controlla se è passata più di una settimana
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        
        setShowBackupWarning(lastBackupDate < sevenDaysAgo);
      } else {
        setShowBackupWarning(true);
      }

      // Recupera lo storico dei backup
      const { data: historyData, error: historyError } = await supabase
        .from('backup_log')
        .select('id, backup_type, filename, backup_date')
        .order('backup_date', { ascending: false })
        .limit(20);

      if (historyError) throw historyError;

      setBackupHistory(historyData || []);
    } catch (err) {
      console.error('Errore nel recuperare le informazioni di backup:', err);
      setError(`Errore nel recuperare le informazioni: ${err.message}`);
    }
  };

  // Effettua il backup
  const performBackup = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const { data: { user } } = await supabase.auth.getUser();

      // Genera timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');

      // Genera lo script SQL completo
      const schemaSql = await generateSchemaSql();
      const schemaBlob = new Blob([schemaSql], { type: 'text/sql;charset=utf-8;' });
      const schemaFilename = `backup_completo_${timestamp}.sql`;

      // Scarica file schema
      const schemaLink = document.createElement('a');
      schemaLink.href = URL.createObjectURL(schemaBlob);
      schemaLink.download = schemaFilename;
      document.body.appendChild(schemaLink);
      schemaLink.click();
      document.body.removeChild(schemaLink);

      // Registra il backup nel database
      const { error: backupLogError } = await supabase
        .from('backup_log')
        .insert({
          backup_type: 'completo',
          filename: schemaFilename,
          user_id: user.id,
          notes: 'Backup completo di schema e dati'
        });

      if (backupLogError) throw backupLogError;

      setSuccess('Backup completo dei dati completato con successo!');

      // Emette l'evento personalizzato "backup-performed"
      window.dispatchEvent(new CustomEvent('backup-performed'));

      // Aggiorna lo stato dell'ultimo backup
      fetchBackupInfo();
    } catch (err) {
      setError(`Errore durante il backup: ${err.message}`);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Carica le informazioni di backup all'inizializzazione
  useEffect(() => {
    fetchBackupInfo();
  }, []);

  return (
    <>
      {showBackupWarning && <BackupWarning />}
      
      <div className="bg-white p-6 rounded-lg shadow-md">
        {/* Tabs per cambiare vista */}
        <div className="flex mb-4 border-b">
          <button
            className={`px-4 py-2 ${activeView === 'backup' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
            onClick={() => setActiveView('backup')}
          >
            Esegui Backup
          </button>
          <button
            className={`px-4 py-2 ${activeView === 'history' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
            onClick={() => setActiveView('history')}
          >
            Storico Backup
          </button>
        </div>

        {activeView === 'backup' ? (
          <>
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <Database size={24} className="mr-2" />
              Backup Database
            </h2>
            
            <div className="mb-4">
              <p className="text-gray-600">
                Esegui un backup completo del database. 
                Verrà generato un file SQL esportabile con schema e dati.
              </p>
              
              {lastBackup && (
                <p className="text-sm text-gray-500 mt-2">
                  Ultimo backup: {lastBackup.toLocaleString()}
                </p>
              )}
            </div>
            
            {error && (
              <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}
            
            {success && (
              <div className="bg-green-50 border border-green-300 text-green-700 px-4 py-3 rounded mb-4">
                {success}
              </div>
            )}
            
            <button
              onClick={performBackup}
              disabled={loading}
              className="bg-blue-600 text-white px-4 py-2 rounded inline-flex items-center hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <span className="mr-2">Backup in corso...</span>
                  <FileDown size={20} />
                </>
              ) : (
                <>
                  <FileDown size={20} className="mr-2" />
                  Esporta Backup Completo
                </>
              )}
            </button>
            
            <div className="mt-4 text-sm text-gray-500">
              <p>
                <strong>Nota:</strong> Questo backup include schema e dati completi del database. 
                Conservare i file di backup in un luogo sicuro.
              </p>
            </div>
          </>
        ) : (
          <div>
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <Clock size={24} className="mr-2" />
              Storico Backup
            </h2>
            
            {backupHistory.length === 0 ? (
              <p className="text-gray-500">Nessun backup eseguito</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="py-2 px-3 text-left">Data</th>
                      <th className="py-2 px-3 text-left">Tipo</th>
                      <th className="py-2 px-3 text-left">Nome File</th>
                    </tr>
                  </thead>
                  <tbody>
                    {backupHistory.map((backup) => (
                      <tr key={backup.id} className="border-t hover:bg-gray-50">
                        <td className="py-2 px-3">
                          {new Date(backup.backup_date).toLocaleString()}
                        </td>
                        <td className="py-2 px-3 uppercase">{backup.backup_type}</td>
                        <td className="py-2 px-3">{backup.filename}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default AdvancedBackup;